import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import LoginForm from './components/LoginForm';

import TrustIndicators from './components/TrustIndicators';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import SEOHead from '../../components/SEOHead';

const UserLogin = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Get trip context from location state
  const tripData = location?.state?.tripData;
  const returnTo = location?.state?.returnTo;
  const autoConfirmTrip = location?.state?.autoConfirmTrip;
  const emailSent = location?.state?.emailSent;
  const email = location?.state?.email;
  
  // Check if user just verified their email
  const verified = searchParams?.get('verified') === 'true';

  const { user, signIn } = useAuth();

  // Helper function to safely convert date to ISO string
  const safeISODate = (dateValue) => {
    if (!dateValue) return new Date()?.toISOString()?.split('T')?.[0];

    const date = new Date(dateValue);
    if (isNaN(date?.getTime())) {
      // Invalid date, return today's date
      return new Date()?.toISOString()?.split('T')?.[0];
    }

    return date?.toISOString()?.split('T')?.[0];
  };

  useEffect(() => {
    // Show success message if email was just verified
    if (verified) {
      setSuccessMessage('Email verified successfully! You can now sign in.');
      // Clear URL parameter
      window?.history?.replaceState({}, '', '/user-login');
    } else if (emailSent && email) {
      setSuccessMessage(`Verification email sent to ${email}. Please check your inbox and click the link to verify your account.`);
    }
  }, [verified, emailSent, email]);

  useEffect(() => {
    // Enhanced redirect logic for trip confirmation flow
    if (user) {
      if (returnTo && tripData && autoConfirmTrip) {
        // NEW: Auto-confirm trip and navigate to detail view
        const confirmTripAndNavigate = async () => {
          try {
            // Prepare trip data for database with safe date conversion
            const dbTripData = {
              destination: tripData?.featuredParks?.[0] || 'AI-Generated Adventure',
              park: tripData?.featuredParks?.join(', ') || 'Multiple State Parks',
              description: `${tripData?.duration || '3-day'} adventure featuring ${tripData?.activityTypes?.join(', ') || 'outdoor activities'}`,
              startDate: safeISODate(tripData?.itinerary?.[0]?.date),
              endDate: safeISODate(tripData?.itinerary?.[tripData?.itinerary?.length - 1]?.date),
              travelers: 2,
              status: 'upcoming',
              imageUrl: tripData?.itinerary?.[0]?.parkImage || 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e',
              imageAlt: tripData?.itinerary?.[0]?.parkImageAlt || 'Beautiful state park landscape',
              totalCost: parseFloat(tripData?.estimatedCost?.match(/\d+/)?.[0]) || 500,
              activities: tripData?.activityTypes || ['Hiking', 'Camping', 'Nature Photography'],
              accommodation: 'Camping',
              notes: `Weather forecast: ${tripData?.weatherForecast || 'Check local conditions'}`,
              isPublic: false
            };

            // Import services dynamically
            const { tripService } = await import('../../services/tripService');
            const { itineraryService } = await import('../../services/itineraryService');

            // Convert AI trip data to itinerary format
            const itineraryItems = itineraryService?.convertAITripToItinerary(tripData);

            // Create trip with itinerary
            const createdTrip = await tripService?.createTripWithItinerary(
              dbTripData,
              itineraryItems,
              tripData
            );

            // Navigate directly to trip detail view
            navigate(`/trip-detail-view/${createdTrip?.id}`, {
              state: {
                tripId: createdTrip?.id,
                tripData: createdTrip,
                isNewTrip: true,
                showConfirmationMessage: true
              },
              replace: true
            });
          } catch (err) {
            console.error('Auto-confirm trip error:', err);
            // Fallback to trip confirmation page if error
            navigate(returnTo, {
              state: { tripData },
              replace: true
            });
          }
        };

        confirmTripAndNavigate();
      } else if (returnTo && tripData) {
        // User logged in from trip confirmation flow (without auto-confirm)
        navigate(returnTo, {
          state: { tripData },
          replace: true
        });
      } else {
        // Normal login flow
        const from = location?.state?.from?.pathname || '/user-dashboard';
        navigate(from, { replace: true });
      }
    }
  }, [user, navigate, location, returnTo, tripData, autoConfirmTrip]);

  const handleLogin = async (formData) => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error } = await signIn(formData?.email, formData?.password);

      if (error) {
        setError(error?.message);
        return;
      }

      // Success will be handled by the useEffect above via auth context
    } catch (err) {
      console.error('Login error:', err);
      setError(err?.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // For now, redirect to Google login (implement social auth later)
      setError('Google login will be available soon. Please use email login.');
    } catch (err) {
      setError('Google login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFacebookLogin = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // For now, redirect to Facebook login (implement social auth later)
      setError('Facebook login will be available soon. Please use email login.');
    } catch (err) {
      setError('Facebook login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEOHead
        pageType="login"
        title="Sign In - Access Your Adventures | ParkTrip AI"
        description="Sign in to ParkTrip Planner AI to access your saved trips, create new itineraries, and discover personalized state park recommendations."
        canonicalUrl="https://parktripplannerai.com/user-login"
        noindex={true}
        keywords={["login", "sign in", "user authentication", "ParkTrip AI"]}
        ogImage="https://images.unsplash.com/photo-1631541542525-33eb98e958c4"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Sign In - ParkTrip AI"
        }}
      />

      <div className="min-h-screen bg-background">
        <Header
          isAuthenticated={false}
          user={null}
          onLogout={() => {}} />

        <main className="pt-16">
          <div className="min-h-screen flex">
            {/* Left Side - Hero Image */}
            <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20"></div>
              <Image
                src="https://images.unsplash.com/photo-1631541542525-33eb98e958c4"
                alt="Scenic forest path with tall trees creating natural archway leading to bright clearing ahead"
                className="w-full h-full object-cover" />

              <div className="absolute inset-0 bg-black/20"></div>
              <div className="absolute bottom-8 left-8 right-8 text-white">
                <h2 className="font-heading font-heading-bold text-3xl mb-4">
                  {tripData ? 'Complete Your Trip Planning' : 'Welcome Back, Explorer!'}
                </h2>
                <p className="font-body text-lg opacity-90 leading-relaxed">
                  {tripData ?
                  "Sign in to save your itinerary and access it anytime from your dashboard. Your perfect state park adventure awaits!" : "Ready to continue your adventure planning? Sign in to access your saved trips, create new itineraries, and discover your next great outdoor experience."}
                </p>
                <div className="flex items-center mt-6 space-x-6">
                  <div className="flex items-center space-x-2">
                    <Icon name="Shield" size={20} />
                    <span className="text-sm">Secure Login</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Bookmark" size={20} />
                    <span className="text-sm">Saved Itineraries</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Zap" size={20} />
                    <span className="text-sm">AI Recommendations</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Login Form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
              <div className="w-full max-w-md">
                <div className="text-center mb-8">
                  <div className="flex justify-center mb-4">
                    <div className="w-16 h-16 bg-primary rounded-organic flex items-center justify-center shadow-organic-md">
                      <Icon name={tripData ? "MapPin" : "Trees"} size={32} color="white" strokeWidth={2} />
                    </div>
                  </div>
                  <h1 className="font-heading font-heading-bold text-3xl text-foreground mb-2">
                    {tripData ? 'Save Your Adventure' : 'Welcome Back'}
                  </h1>
                  <p className="font-body text-muted-foreground">
                    {tripData ?
                    "Sign in to save your trip itinerary permanently" : "Sign in to access your saved trips and continue planning"}
                  </p>
                </div>

                {/* Success Message Banner */}
                {successMessage && (
                  <div className="bg-success/10 border border-success/20 rounded-organic p-4 mb-6">
                    <div className="flex items-center space-x-3">
                      <Icon name="CheckCircle" size={16} color="var(--color-success)" />
                      <p className="text-sm text-success">{successMessage}</p>
                    </div>
                  </div>
                )}

                {/* Trip Context Banner */}
                {tripData && (
                <div className="bg-primary/10 border border-primary/20 rounded-organic p-4 mb-6">
                    <div className="flex items-center space-x-3">
                      <Icon name="Save" size={16} color="var(--color-primary)" />
                      <div>
                        <p className="text-sm font-body font-body-medium text-card-foreground">
                          Ready to save: {tripData?.featuredParks?.[0] || 'State Park'} Trip
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Sign in to keep this itinerary in your account forever
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <LoginForm
                  onLogin={handleLogin}
                  isLoading={isLoading}
                  error={error} />

                <TrustIndicators />

                {tripData && (
                <div className="mt-6 pt-4 border-t border-border">
                    <button
                    onClick={() => navigate('/trip-confirmation', { state: { tripData } })}
                    className="w-full py-2 text-sm text-muted-foreground hover:text-primary transition-colors">

                      ← Back to Trip Summary
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default UserLogin;